import { type DownloadPriority } from "@/shared/reader";
import { type MessagePayload } from "@/shared/webworker";

import { type ColumnDefinition } from "../columnFilters/columnDefinition";
import { IndexState } from "../IndexState";

export enum LogEvent {
  ColumnsChanged = "columns-changed",
  Error = "error",
  Initialized = "initialized",
  LoadingStateChange = "loading-state-change",
  LogsBuffered = "logs-buffered",
  LogTimeIndexStateChange = "logtime-index-state-change",
  NumLogsChanged = "num-logs-changed",
  SearchIndexStateChange = "search-index-state-change",
}

export interface LogEventPayloadMap {
  [LogEvent.ColumnsChanged]: { columns: ColumnDefinition[] };
  [LogEvent.Error]: Error;
  [LogEvent.Initialized]: undefined;
  [LogEvent.LoadingStateChange]: { state: DownloadPriority | false };
  [LogEvent.LogsBuffered]: { count: number };
  [LogEvent.LogTimeIndexStateChange]: {
    state: IndexState;
    /** ArrayBuffer containing BigUint64Array of timestamps per display position */
    timestamps?: ArrayBuffer;
    /** ArrayBuffer containing Float64Array of sequences per display position */
    sequences?: ArrayBuffer;
  };
  [LogEvent.NumLogsChanged]: { numLogs: number };
  [LogEvent.SearchIndexStateChange]: { state: IndexState };
}

export function isColumnsChangedEvent(
  event: MessageEvent<MessagePayload>,
): event is MessageEvent<
  MessagePayload<LogEvent, LogEventPayloadMap[LogEvent.ColumnsChanged]>
> {
  return event.data.type === LogEvent.ColumnsChanged;
}

export function isInitializedEvent(
  event: MessageEvent<MessagePayload>,
): event is MessageEvent<
  MessagePayload<LogEvent, LogEventPayloadMap[LogEvent.Initialized]>
> {
  return event.data.type === LogEvent.Initialized;
}

export function isLoadingStateChangeEvent(
  event: MessageEvent<MessagePayload>,
): event is MessageEvent<
  MessagePayload<LogEvent, LogEventPayloadMap[LogEvent.LoadingStateChange]>
> {
  return event.data.type === LogEvent.LoadingStateChange;
}

export function isLogTimeIndexStateChangeEvent(
  event: MessageEvent<MessagePayload>,
): event is MessageEvent<
  MessagePayload<LogEvent, LogEventPayloadMap[LogEvent.LogTimeIndexStateChange]>
> {
  return event.data.type === LogEvent.LogTimeIndexStateChange;
}

export function isSearchIndexStateChangeEvent(
  event: MessageEvent<MessagePayload>,
): event is MessageEvent<
  MessagePayload<LogEvent, LogEventPayloadMap[LogEvent.SearchIndexStateChange]>
> {
  return event.data.type === LogEvent.SearchIndexStateChange;
}
